import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companyMenuApiClient } from '@src/api/company/menu/companyMenuApiClient.ts';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import CommunityMenubarForTenant from '@src/features/shared/community/components/CommunityMenubarForTenant';
import CommunityMobileHeaderForTenant from '@src/features/shared/community/components/CommunityMobileHeaderForTenant';
import CommunitySidebar from '@src/features/shared/community/components/CommunitySidebar';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { useVirtualKeyboard } from '@src/hooks/useVirtualKeyboard';
import MobileHeader from '@src/ui/templates/Company/MobileHeader/MobileHeader';
import CompanyMenubar from '@src/ui/templates/Company/Navbar/CompanyMenubar';
import { NavItem } from '@src/ui/templates/Company/NavMenu/types';
// import TenantNavBar from '@src/ui/templates/Tenant/Header/TenantNavBar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

type Props = {
  children: React.ReactNode;
  pageName?: string;
  showSidebar?: boolean;
};

function CommunityDefaultLayout({ children, pageName, showSidebar = true }: Props) {
  const location = useLocation();
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const tenantAuth = useTenantAuth();
  const [openMobileSidebar, setOpenMobileSidebar] = useState<boolean>(false);
  const { code: channelCode } = useParams<{ code: string }>();

  const isMobile = useIsMobile(1024);
  const { isVisible: isVirtualKeyboardVisible } = useVirtualKeyboard();

  const handleMobileMenuCloseClick = () => {
    setOpenMobileSidebar(false);
  };

  const toggleMobileSidebar = () => {
    setOpenMobileSidebar(!openMobileSidebar);
  };

  const shouldHideHeader = isMobile && isVirtualKeyboardVisible;

  const baseSidebarClassName = useMemo(
    () =>
      `flex flex-col lg:w-full${showSidebar ? ' w-[calc(100%-240px)] xl:w-[calc(100%-200px)]' : ' xl:w-full'}`,
    [showSidebar],
  );

  const sidebarClassName = useMemo(
    () => `${baseSidebarClassName}${shouldHideHeader ? '' : ' lg:pt-[60px]'}`,
    [baseSidebarClassName, shouldHideHeader],
  );

  const updateCurrentNavItems = useCallback(() => {
    const currentPath = location.pathname;
    setNavItems((prevNavItems) =>
      prevNavItems.map((item) => ({
        ...item,
        current: item.link ? currentPath.startsWith(item.link) : false,
      })),
    );
  }, [location.pathname]);

  const fetchCompanyMenu = useCallback(async () => {
    const res = await companyMenuApiClient.index();
    handleApiResponse(
      res,
      (data) => {
        const navItems = data.map((menu) => ({
          title: menu.title,
          link: menu.link ?? '/',
          adminOnly: menu.adminOnly,
          isComingSoon: menu.isComingSoon,
          isLocked: menu.isLocked,
          current: menu.link ? location.pathname.startsWith(menu.link) : false,
        }));
        setNavItems(navItems);
      },
      (error) => {
        console.error('メニューの取得に失敗しました:', error);
      },
    );
  }, [location.pathname]);

  useEffect(() => {
    fetchCompanyMenu();
  }, [fetchCompanyMenu, pageName]);

  useEffect(() => {
    updateCurrentNavItems();
  }, [updateCurrentNavItems]);

  useEffect(() => {
    if (openMobileSidebar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [openMobileSidebar]);

  if (!tenantAuth.profile) {
    return null;
  }

  const isTenant = tenantAuth.profile?.isLogin;

  return (
    <MobileSidebarContext.Provider
      value={{ isOpen: openMobileSidebar, toggle: toggleMobileSidebar }}
    >
      <div className='min-h-dvh'>
        <div className='flex lg:block'>
          {/* PCサイドバー */}
          {!isMobile && (
            <div className='sticky top-0 h-dvh overflow-hidden lg:hidden'>
              {showSidebar && (
                <div className='h-dvh w-[240px] shrink-0 overflow-y-auto xl:w-[200px]'>
                  <CommunitySidebar
                    currentChannelCode={channelCode}
                    isTenant={isTenant}
                  />
                </div>
              )}
            </div>
          )}

          {navItems.length > 0 && (
            <>
              {/* モバイルヘッダー */}
              {!shouldHideHeader && (
                <div className='lg:block'>
                  {isTenant ? (
                    <CommunityMobileHeaderForTenant />
                  ) : (
                    <MobileHeader navItems={navItems} />
                  )}
                </div>
              )}
              <div className={sidebarClassName}>
                {/* PCヘッダー */}
                {!isMobile &&
                  (isTenant ? (
                    <CommunityMenubarForTenant />
                  ) : (
                    <CompanyMenubar navItems={navItems} />
                  ))}
                {/* メインコンテンツ */}
                <div className='grow'>{children}</div>
              </div>
            </>
          )}

          {/* モバイルサイドバー */}
          {isMobile && (
            <div
              className={`
                absolute top-0 z-[99999] hidden h-dvh w-full overflow-y-auto bg-white transition-all lg:block
                ${openMobileSidebar ? 'left-0 opacity-100' : '-left-full opacity-0'}
              `}
            >
              <CommunitySidebar
                handleCloseClick={handleMobileMenuCloseClick}
                currentChannelCode={channelCode}
              />
            </div>
          )}
        </div>
      </div>
    </MobileSidebarContext.Provider>
  );
}

export default CommunityDefaultLayout;
